import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Triangle, CheckCircle, XCircle, WarningCircle } from "@phosphor-icons/react";

const riskClass = (r) => r === "alto" ? "risk-alto" : r === "medio" ? "risk-medio" : "risk-baixo";

export default function LogoComparator() {
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState("");
  const [variationId, setVariationId] = useState("");
  const [results, setResults] = useState([]);
  const [selectedId, setSelectedId] = useState("");

  useEffect(() => { api.get("/brands").then(({data}) => { setBrands(data); if (data.length) setBrandId(data[0].id); }); }, []);
  useEffect(() => {
    if (!brandId) return;
    api.get("/results", { params: { brand_id: brandId } }).then(({data}) => {
      const withVisual = data.filter(r => r.similaridade_visual > 0);
      setResults(withVisual);
      if (withVisual.length) setSelectedId(withVisual[0].id);
    });
    // reset variation selection when brand changes
    setVariationId("");
  }, [brandId]);

  const brand = brands.find(b => b.id === brandId);
  const result = results.find(r => r.id === selectedId);
  const variation = brand?.logo_variations?.find(v => v.id === variationId);
  const referenceUrl = variation?.url || brand?.logo_url;

  const updateStatus = async (status) => {
    if (!result) return;
    await api.patch(`/results/${result.id}`, { status });
    toast.success("Status atualizado");
    api.get("/results", { params: { brand_id: brandId } }).then(({data}) => setResults(data.filter(r => r.similaridade_visual > 0)));
  };

  return (
    <div className="space-y-6" data-testid="comparator-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Comparador de logos</div>
        <h1 className="font-heading font-extrabold text-4xl">Análise visual lado a lado</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label className="text-xs uppercase">Marca de referência</Label>
          <Select value={brandId} onValueChange={setBrandId}>
            <SelectTrigger data-testid="cmp-brand-select" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>{brands.map(b => <SelectItem key={b.id} value={b.id}>{b.nome_marca}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase">Variação do logotipo</Label>
          <Select value={variationId || "primary"} onValueChange={(v) => setVariationId(v === "primary" ? "" : v)}>
            <SelectTrigger data-testid="cmp-variation-select" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="primary">Logo principal</SelectItem>
              {(brand?.logo_variations || []).map(v => <SelectItem key={v.id} value={v.id}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase">Resultado a comparar</Label>
          <Select value={selectedId} onValueChange={setSelectedId}>
            <SelectTrigger data-testid="cmp-result-select" className="rounded-sm mt-1"><SelectValue placeholder="Selecione" /></SelectTrigger>
            <SelectContent>{results.map(r => <SelectItem key={r.id} value={r.id}>{r.nome_encontrado} — {r.similaridade_visual}%</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {result && brand && (
        <Card className="p-6 rounded-sm border">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-6 items-center">
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">Logo oficial{variation ? ` — ${variation.label}` : ""}</div>
              <div className="aspect-square bg-white border rounded-sm flex items-center justify-center p-8">
                {referenceUrl ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${referenceUrl}`} alt="oficial" className="max-w-full max-h-full object-contain" />
                ) : (
                  <Triangle size={120} weight="bold" style={{ color: brand.cor_principal || "#0A3B2C" }} />
                )}
              </div>
              <div className="mt-3 font-heading font-bold">{brand.nome_marca}</div>
              <div className="text-xs text-muted-foreground">{brand.segmento}</div>
            </div>

            <div className="flex flex-col items-center gap-2">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Similaridade visual</div>
              <div className={`w-24 h-24 rounded-full border-4 flex items-center justify-center font-heading font-extrabold text-2xl ${result.similaridade_visual >= 70 ? "border-[#DC2626] text-[#DC2626]" : result.similaridade_visual >= 40 ? "border-[#B45309] text-[#B45309]" : "border-[#047857] text-[#047857]"}`}>
                {result.similaridade_visual}%
              </div>
            </div>

            <div className="text-center">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">Logo encontrado</div>
              <div className="aspect-square bg-white border rounded-sm flex items-center justify-center p-8">
                <div className="text-center opacity-60">
                  <Triangle size={80} weight="bold" className="mx-auto text-gray-400" />
                  <div className="text-[10px] text-muted-foreground mt-2">Imagem demonstrativa</div>
                </div>
              </div>
              <div className="mt-3 font-heading font-bold">{result.nome_encontrado}</div>
              <div className="text-xs text-muted-foreground">{result.segmento}</div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-8 pt-6 border-t">
            <ComparisonRow label="Forma" value={result.similaridade_visual} />
            <ComparisonRow label="Cor" value={Math.max(0, result.similaridade_visual - 10)} />
            <ComparisonRow label="Estrutura interna" value={Math.max(0, result.similaridade_visual - 15)} />
            <ComparisonRow label="Tipografia" value={Math.min(100, result.similaridade_visual - 5)} />
            <ComparisonRow label="Nome" value={result.similaridade_nome} />
            <ComparisonRow label="Segmento" value={result.segmento === brand.segmento ? 100 : 0} />
          </div>

          <div className="flex flex-wrap gap-2 mt-6">
            <Button data-testid="cmp-confirm" onClick={() => updateStatus("conflito_confirmado")} className="rounded-sm bg-[#DC2626] hover:bg-red-700">
              <WarningCircle size={14} className="mr-2" /> Confirmar possível conflito
            </Button>
            <Button data-testid="cmp-review" onClick={() => updateStatus("revisado")} variant="outline" className="rounded-sm">
              Marcar para revisão
            </Button>
            <Button data-testid="cmp-discard" onClick={() => updateStatus("descartado")} variant="outline" className="rounded-sm">
              <XCircle size={14} className="mr-2" /> Descartar
            </Button>
          </div>

          <div className={`mt-6 p-3 rounded-sm ${riskClass(result.grau_risco)}`}>
            <div className="text-[10px] uppercase font-bold">Grau de risco atribuído</div>
            <div className="font-heading font-bold text-lg">{result.grau_risco.toUpperCase()}</div>
            <div className="text-xs mt-1">{result.observacao_ia}</div>
          </div>

          <div className="text-[10px] text-muted-foreground mt-3 border-t pt-2">
            Esta classificação é informativa e não substitui análise jurídica especializada.
          </div>
        </Card>
      )}

      {!result && <Card className="p-8 text-center text-sm text-muted-foreground rounded-sm">Nenhum resultado com semelhança visual disponível para esta marca.</Card>}
    </div>
  );
}

const ComparisonRow = ({ label, value }) => (
  <div>
    <div className="flex justify-between text-xs mb-1">
      <span className="text-muted-foreground uppercase text-[10px]">{label}</span>
      <span className="font-mono font-semibold">{Math.round(value)}%</span>
    </div>
    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
      <div className="h-full bg-[#0A3B2C]" style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
    </div>
  </div>
);
